import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";
import DataDeletion from "@/pages/DataDeletion";
import SalonLayout from "@/components/SalonLayout";
import Dashboard from "@/pages/salon/Dashboard";
import Agenda from "@/pages/salon/Agenda";
import Clients from "@/pages/salon/Clients";
import ClientDetail from "@/pages/salon/ClientDetail";
import Tags from "@/pages/salon/Tags";
import Services from "@/pages/salon/Services";
import Staff from "@/pages/salon/Staff";
import StaffPerformance from "@/pages/salon/StaffPerformance";
import Invoices from "@/pages/salon/Invoices";
import Inventory from "@/pages/salon/Inventory";
import Costs from "@/pages/salon/Costs";
import Settings from "@/pages/salon/Settings";
import Automations from "@/pages/salon/Automations";
import AutomationEditorPage from "@/pages/salon/AutomationEditorPage";
import EmailMarketing from "@/pages/salon/EmailMarketing";
import EmailTemplateEditor from "@/pages/salon/EmailTemplateEditor";
import Pipeline from "@/pages/salon/Pipeline";
import Inbox from "@/pages/salon/Inbox";
import Publish from "@/pages/salon/Publish";
import AdsKPI from "@/pages/salon/AdsKPI";
import TaxCalculator from "@/pages/salon/TaxCalculator";
import Formazione from "@/pages/salon/Formazione";
import CourseDetail from "@/pages/salon/CourseDetail";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfService from "@/pages/legal/TermsOfService";
import AcceptInvite from "@/pages/AcceptInvite";
import { PermGuard } from "@/components/PermGuard";
import { CookieBanner } from "@/components/CookieBanner";
import AgencyDashboard from "@/pages/admin/AgencyDashboard";
import AdminOverview from "@/pages/admin/AdminOverview";
import AgencyBilling from "@/pages/admin/AgencyBilling";
import AgencyUsers from "@/pages/admin/AgencyUsers";
import AgencyFormazione from "@/pages/admin/AgencyFormazione";
import AgencyTenantDetail from "@/pages/admin/AgencyTenantDetail";
import AgencyClients from "@/pages/admin/AgencyClients";
import AgencySettings from "@/pages/admin/AgencySettings";
import AgencyWorkspaceLayout from "@/pages/admin/agency/AgencyWorkspaceLayout";
import AgencyPipelinePage from "@/pages/admin/agency/AgencyPipelinePage";
import AgencyLeadInboxPage from "@/pages/admin/agency/AgencyLeadInboxPage";
import AgencyCalendarPage from "@/pages/admin/agency/AgencyCalendarPage";
import AgencyCallbacksPage from "@/pages/admin/agency/AgencyCallbacksPage";
import ReceptionistSettings from "@/pages/admin/agency/ReceptionistSettings";
import AgencyOverviewPage from "@/pages/admin/agency/AgencyOverviewPage";
import AgencyAdsPerformance from "@/pages/admin/AgencyAdsPerformance";
import AgencyAdsTenantDetail from "@/pages/admin/AgencyAdsTenantDetail";
import AgencyAiBotPage from "@/pages/admin/agency/AgencyAiBotPage";
import AgencyPermGuard from "@/components/AgencyPermGuard";
import { AGENCY_PERMS } from "@/lib/agencyPerms";

function homeFor(role) {
  if (role === "agency_admin") return "/admin";
  if (role === "agency_receptionist") return "/admin/agency/pipeline";
  return "/app";
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-10 text-sm text-[color:var(--sp-text-secondary)]">Caricamento…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // Salon owner without tenant → must complete onboarding first
  if (user.role === "salon_owner" && !user.tenant_id && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  if (roles && !roles.includes(user.role)) {
    // Route mismatch → invia l'utente alla sua home area (evita redirect loop admin ↔ app)
    return <Navigate to={homeFor(user.role)} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  return <Navigate to={homeFor(user.role)} replace />;
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth callback synchronously (URL hash)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/legal/privacy" element={<PrivacyPolicy />} />
      <Route path="/legal/terms" element={<TermsOfService />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />

      {/* Pagine pubbliche (Meta verification) */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/data-deletion" element={<DataDeletion />} />

      {/* Salon owner area */}
      <Route path="/app" element={<Protected roles={["salon_owner", "staff"]}><SalonLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="agenda" element={<PermGuard perm="agenda"><Agenda /></PermGuard>} />
        <Route path="clients" element={<PermGuard perm="clients"><Clients /></PermGuard>} />
        <Route path="clients/:id" element={<PermGuard perm="clients"><ClientDetail /></PermGuard>} />
        <Route path="tags" element={<PermGuard perm="clients"><Tags /></PermGuard>} />
        <Route path="services" element={<PermGuard perm="services"><Services /></PermGuard>} />
        <Route path="staff" element={<PermGuard perm="staff_performance"><Staff /></PermGuard>} />
        <Route path="staff-performance" element={<PermGuard perm="staff_performance"><StaffPerformance /></PermGuard>} />
        <Route path="invoices" element={<PermGuard perm="invoices"><Invoices /></PermGuard>} />
        <Route path="inventory" element={<PermGuard perm="warehouse"><Inventory /></PermGuard>} />
        <Route path="costs" element={<PermGuard perm="costs"><Costs /></PermGuard>} />
        <Route path="automations" element={<PermGuard perm="automations"><Automations /></PermGuard>} />
        <Route path="automations/:id" element={<PermGuard perm="automations"><AutomationEditorPage /></PermGuard>} />
        <Route path="email" element={<PermGuard perm="email_marketing"><EmailMarketing /></PermGuard>} />
        <Route path="email/templates/:id" element={<PermGuard perm="email_marketing"><EmailTemplateEditor /></PermGuard>} />
        <Route path="pipeline" element={<PermGuard perm="pipeline"><Pipeline /></PermGuard>} />
        <Route path="inbox" element={<PermGuard perm="inbox"><Inbox /></PermGuard>} />
        <Route path="publish" element={<PermGuard perm="publish"><Publish /></PermGuard>} />
        <Route path="ads-kpi" element={<PermGuard perm="invoices"><AdsKPI /></PermGuard>} />
        <Route path="tax-calculator" element={<PermGuard perm="costs"><TaxCalculator /></PermGuard>} />
        <Route path="formazione" element={<PermGuard perm="formazione"><Formazione /></PermGuard>} />
        <Route path="formazione/:id" element={<PermGuard perm="formazione"><CourseDetail /></PermGuard>} />
        <Route path="settings" element={<PermGuard perm="settings"><Settings /></PermGuard>} />
      </Route>

      {/* Agency admin area — include Agency Workspace come sezione annidata,
           così eredita SalonLayout admin (sidebar/header coerenti). */}
      <Route path="/admin" element={<Protected roles={["agency_admin", "agency_receptionist"]}><SalonLayout admin /></Protected>}>
        <Route index element={<AdminOverview />} />
        <Route path="agency" element={<AgencyWorkspaceLayout />}>
          <Route index element={<AgencyPermGuard perm={AGENCY_PERMS.overview}><AgencyOverviewPage /></AgencyPermGuard>} />
          <Route path="pipeline" element={<AgencyPermGuard perm={AGENCY_PERMS.pipeline}><AgencyPipelinePage /></AgencyPermGuard>} />
          <Route path="lead-inbox" element={<AgencyPermGuard perm={AGENCY_PERMS.leadInbox}><AgencyLeadInboxPage /></AgencyPermGuard>} />
          <Route path="calendario" element={<AgencyPermGuard perm={AGENCY_PERMS.calendar}><AgencyCalendarPage /></AgencyPermGuard>} />
          <Route path="richiami" element={<AgencyPermGuard perm={AGENCY_PERMS.callbacks}><AgencyCallbacksPage /></AgencyPermGuard>} />
          <Route path="ai-bot" element={<AgencyPermGuard perm={AGENCY_PERMS.aiBot}><AgencyAiBotPage /></AgencyPermGuard>} />
          <Route path="impostazioni" element={<AgencyPermGuard perm={AGENCY_PERMS.settings}><ReceptionistSettings /></AgencyPermGuard>} />
        </Route>
        <Route path="comparison" element={<AgencyDashboard />} />
        <Route path="users" element={<AgencyUsers />} />
        <Route path="formazione" element={<AgencyFormazione />} />
        <Route path="billing" element={<AgencyBilling />} />
        <Route path="tenants/:id" element={<AgencyTenantDetail />} />
        <Route path="clienti" element={<AgencyClients />} />
        <Route path="clienti/:id" element={<AgencyClients />} />
        <Route path="ads-performance" element={<AgencyAdsPerformance />} />
        <Route path="ads-performance/:tenantId" element={<AgencyAdsTenantDetail />} />
        <Route path="settings" element={<AgencySettings />} />
        {/* Shared with tenant area — auto-scoped to the Agency HQ workspace via
             the ``X-Workspace: agency-hq`` header injected by src/lib/api.js */}
        <Route path="clients" element={<Clients />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="tags" element={<Tags />} />
        <Route path="publish" element={<Publish />} />
        <Route path="inbox" element={<Inbox />} />
        <Route path="email" element={<EmailMarketing />} />
        <Route path="email/templates/:id" element={<EmailTemplateEditor />} />
        <Route path="automations" element={<Automations />} />
        <Route path="automations/:id" element={<AutomationEditorPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <AppRouter />
        <CookieBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}
